import { DocumentIDValue, DocumentValue, Store } from '@dittolive/ditto'
import { uuid } from '@portal/components'
import * as Sentry from '@sentry/browser'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { invariant } from 'utils'

import {
  globalIssueSchema,
  issueSchema,
  MutableField,
  newCommentSchema,
  publicFieldsRules,
} from './schemas'
import {
  Actor,
  AppConfig,
  ClosedEvent,
  DittoIssuesManagementClient,
  Event,
  IssueStatus,
  IssueToCreate,
  NewCommentEvent,
  ReopenedEvent,
  UpdatedFieldEvent,
} from './types'

dayjs.extend(utc)

export const Timeline = {
  getUpdateFieldEvent: (
    name: string,
    from: unknown,
    to: unknown,
    actor: Actor,
  ): UpdatedFieldEvent => {
    return {
      type: 'UpdatedFieldEvent',
      name,
      createdAt: dayjs().utc().valueOf(),
      actorId: actor.id,
      from,
      to,
      id: uuid(),
    }
  },
  getReopenedEvent: (actor: Actor): ReopenedEvent => {
    return {
      type: 'ReopenedEvent',
      actorId: actor.id,
      createdAt: dayjs().utc().valueOf(),
      id: uuid(),
    }
  },
  getClosedEvent: (actor: Actor): ClosedEvent => {
    return {
      type: 'ClosedEvent',
      actorId: actor.id,
      createdAt: dayjs().utc().valueOf(),
      id: uuid(),
    }
  },
  getNewCommentEvent: (actor: Actor, commentId: string): NewCommentEvent => {
    return {
      type: 'NewCommentEvent',
      actorId: actor.id,
      createdAt: dayjs().utc().valueOf(),
      commentId,
      id: uuid(),
    }
  },
}

/** Implements issues management functionality for the Ditto store */
class Client implements DittoIssuesManagementClient {
  store: Store
  actor: Actor
  config?: AppConfig

  constructor(store: Store, actor: Actor, config?: AppConfig) {
    invariant(store, 'Ditto store is not available')
    invariant(actor?.id, 'The actor identity is required')
    invariant(actor?.email, 'The actor identity is required')

    if (config) {
      invariant(config.organizationId, 'The organizationId is required')
      invariant(config.appId, 'The appId is required')
    }

    this.store = store
    this.actor = actor
    this.config = config
  }

  private async updateDocumentField(
    id: string,
    name: MutableField,
    value: string,
  ): Promise<DocumentValue> {
    try {
      const validatedValue = await publicFieldsRules[name].validate(value)
      const results = await this.store
        ?.collection('issues')
        .findByID(id)
        .update((document) => {
          let timelineEvent
          if (name === 'status') {
            timelineEvent =
              value === IssueStatus.open
                ? Timeline.getReopenedEvent(this.actor)
                : Timeline.getClosedEvent(this.actor)
          } else {
            timelineEvent = Timeline.getUpdateFieldEvent(
              name,
              document.value[name],
              value,
              this.actor,
            )
          }
          document.at(name).set(validatedValue)
          const dateField =
            name === 'status' && validatedValue === IssueStatus.closed
              ? 'closedAt'
              : 'updatedAt'
          document.at(dateField).set(dayjs().utc().valueOf())
          /** Updates the issue timeline */
          document
            .at('timeline')
            .set([...document.value.timeline, timelineEvent])
        })

      const updatedDocument = await this.store
        .collection('issues')
        .findByID(id)
        .exec()

      /** Check that two fields were updated, and update the local state to not wait to the store observe event */
      if (results && results.length === 3 && updatedDocument) {
        return updatedDocument.value
      }
      return Promise.reject('Issue has not been updated')
    } catch (error) {
      Sentry.withScope(
        (scope: { setTag: (name: string, value: string) => void }) => {
          scope.setTag('issues', `update-${name}`)
          Sentry.captureException(error)
        },
      )
      throw error
    }
  }

  async createIssue(
    title: string,
    body: string,
    platform: string,
    sdkVersion: string,
  ): Promise<DocumentIDValue> {
    const isGlobalIssue = !this.config
    const schema = isGlobalIssue ? globalIssueSchema : issueSchema
    const issue: IssueToCreate = {
      title,
      status: IssueStatus.open,
      comments: [],
      authorId: this.actor.id,
      createdAt: dayjs().utc().valueOf(),
      body,
      labels: [],
      platform,
      sdkVersion,
      timeline: [],
      id: uuid(),
      ...(isGlobalIssue ? {} : this.config),
    }
    try {
      const validated = await schema.validate(issue)
      const document = validated as DocumentValue

      return await this.store.collection('issues').upsert(document)
    } catch (error) {
      Sentry.withScope(
        (scope: { setTag: (name: string, value: string) => void }) => {
          scope.setTag('issues', 'create')
          Sentry.captureException(error)
        },
      )

      throw error
    }
  }

  /** Updates the issue title field */
  async updateTitle(id: string, title: string): Promise<DocumentValue> {
    return this.updateDocumentField(id, 'title', title)
  }
  /** Updates the issue description field */
  async updateDescription(id: string, body: string): Promise<DocumentValue> {
    return this.updateDocumentField(id, 'body', body)
  }
  /** Updates the issue platform field value */
  async updatePlatform(id: string, platform: string): Promise<DocumentValue> {
    return this.updateDocumentField(id, 'platform', platform)
  }
  /** Updates issue SDK version */
  async updateSdkVersion(
    id: string,
    sdkVersion: string,
  ): Promise<DocumentValue> {
    return this.updateDocumentField(id, 'sdkVersion', sdkVersion)
  }
  /** Adds a new comment */
  async addComment(
    id: string,
    body: string,
    options?: { shouldClose?: boolean; shouldReopen?: boolean },
  ): Promise<DocumentValue> {
    try {
      const comment = await newCommentSchema.validate({
        id: uuid(),
        body,
        authorId: this.actor.id,
        createdAt: dayjs().utc().valueOf(),
      })

      const results = await this.store
        ?.collection('issues')
        .findByID(id)
        .update((document) => {
          document.at('comments').set([...document.value.comments, comment])
          const newCommentEvent = Timeline.getNewCommentEvent(
            this.actor,
            comment!.id,
          )
          const timelineEvents: Array<Event> = [newCommentEvent]
          const currentStatus = document.value.status
          if (options?.shouldClose && currentStatus === IssueStatus.open) {
            document.at('status').set(IssueStatus.closed)
            timelineEvents.push(Timeline.getClosedEvent(this.actor))
          }
          if (options?.shouldReopen && currentStatus === IssueStatus.closed) {
            document.at('status').set(IssueStatus.open)
            timelineEvents.push(Timeline.getReopenedEvent(this.actor))
          }

          document
            .at('timeline')
            .set([...document.value.timeline, ...timelineEvents])
        })

      const updatedDocument = await this.store
        .collection('issues')
        .findByID(id)
        .exec()

      if (results && results.length > 0 && updatedDocument) {
        return updatedDocument.value
      }

      return Promise.reject('Comment has not been created')
    } catch (error) {
      Sentry.withScope(
        (scope: { setTag: (name: string, value: string) => void }) => {
          scope.setTag('issues', `update-add-comment`)
          Sentry.captureException(error)
        },
      )

      throw error
    }
  }
  /** Reopen issue */
  async reopenIssue(id: string): Promise<DocumentValue> {
    return this.updateDocumentField(id, 'status', IssueStatus.open)
  }
  /** Close issue */
  async closeIssue(id: string): Promise<DocumentValue> {
    return this.updateDocumentField(id, 'status', IssueStatus.closed)
  }
}

export default Client
